import { API_ORIGIN } from '../config';
import { clearAuthSession, getAuthHeader } from '../auth/session';

function buildUrl(path, params) {
  const base = API_ORIGIN || '';
  const url = new URL(`${base}${path}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      url.searchParams.set(k, String(v));
    });
  }
  // If API_ORIGIN is empty, keep relative URLs (nicer in tests and when served same-origin)
  return API_ORIGIN ? url.toString() : `${path}${url.search}`;
}

async function parseResponse(res) {
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) return res.json();
  return res.text();
}

async function request(method, path, { params, body, headers, signal } = {}) {
  const res = await fetch(buildUrl(path, params), {
    method,
    headers: { ...(getAuthHeader() || {}), ...(headers || {}) },
    body,
    signal,
  });

  const data = await parseResponse(res);
  if (!res.ok) {
    // If the token is invalid/expired (common after server restarts), proactively log out so UI doesn't
    // keep showing "signed in" while every API call fails.
    if (res.status === 401) {
      clearAuthSession();
    }
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return { data };
}

// Multipart upload over XHR so the caller can watch bytes go up; resolves
// like `request` ({ data }) and rejects with err.status / err.data.
function upload(path, formData, { params, headers, signal, onUploadProgress } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', buildUrl(path, params));
    const allHeaders = { ...(getAuthHeader() || {}), ...(headers || {}) };
    Object.entries(allHeaders).forEach(([key, value]) => {
      if (value !== undefined && value !== null) xhr.setRequestHeader(key, String(value));
    });
    const finish = (err) => {
      if (signal) signal.removeEventListener('abort', onAbort);
      if (err) reject(err);
    };
    const onAbort = () => {
      xhr.abort();
      const err = new Error('The user aborted a request.');
      err.name = 'AbortError';
      finish(err);
    };
    if (signal) {
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener('abort', onAbort);
    }
    if (xhr.upload && typeof onUploadProgress === 'function') {
      xhr.upload.addEventListener('progress', (event) => {
        if (!event.lengthComputable) return;
        onUploadProgress(Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100))), event);
      });
      xhr.upload.addEventListener('load', () => onUploadProgress(100));
    }
    xhr.onerror = () => finish(Object.assign(new Error('Network error'), { status: 0 }));
    xhr.onload = () => {
      const contentType = xhr.getResponseHeader('content-type') || '';
      let data = xhr.responseText;
      if (contentType.includes('application/json')) {
        try { data = JSON.parse(xhr.responseText || 'null'); } catch { /* keep text */ }
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        finish(null);
        resolve({ data });
        return;
      }
      if (xhr.status === 401) clearAuthSession();
      const err = new Error(`HTTP ${xhr.status}`);
      err.status = xhr.status;
      err.data = data;
      finish(err);
    };
    xhr.send(formData);
  });
}

export const http = {
  get(path, options) {
    return request('GET', path, options);
  },
  upload,
  post(path, body, options = {}) {
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
    const headers = isFormData
      ? options.headers // browser will set proper multipart boundary
      : { 'Content-Type': 'application/json', ...(options.headers || {}) };

    return request('POST', path, {
      ...options,
      headers,
      body: isFormData ? body : JSON.stringify(body),
    });
  },
  patch(path, body, options = {}) {
    const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
    return request('PATCH', path, { ...options, headers, body: JSON.stringify(body || {}) });
  },
  delete(path, options = {}) {
    return request('DELETE', path, options);
  },
};

